import { useEquipment } from '@/api/queries';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/cn';
import { haptic } from '@/lib/telegram';
import type { Waifu, WaifuEquipmentSlot } from '@/types';

interface Props {
  waifu: Waifu;
  userId: number;
}

const FALLBACK_WEAPON = 'sword_t1.webp';
const FALLBACK_ARMOR = 'plate_t1.webp';

function weaponIconPath(slot: WaifuEquipmentSlot | null | undefined): string {
  if (!slot) return `/data/items/weapons/${FALLBACK_WEAPON}`;
  if (slot.icon) return `/data/items/${slot.icon}`;
  const tier = slot.tier ?? slot.level ?? 1;
  if (slot.weapon_type) {
    return `/data/items/weapons/${slot.weapon_type}_t${tier}.webp`;
  }
  return `/data/items/weapons/${FALLBACK_WEAPON}`;
}

function armorIconPath(slot: WaifuEquipmentSlot | null | undefined): string {
  if (!slot) return `/data/items/armor/${FALLBACK_ARMOR}`;
  if (slot.icon) return `/data/items/${slot.icon}`;
  const tier = slot.tier ?? slot.level ?? 1;
  if (slot.armor_type) {
    return `/data/items/armor/${slot.armor_type}_t${tier}.webp`;
  }
  return `/data/items/armor/${FALLBACK_ARMOR}`;
}

export function WaifuEquipment({ waifu, userId }: Props) {
  const { data, isLoading } = useEquipment(waifu.id, userId);
  const openModal = useUIStore((s) => s.openModal);

  if (isLoading || !data) {
    return <div className="text-white/40 text-sm">Загружаем экипировку…</div>;
  }

  const { equipment, upgrade_costs } = data;
  const canRing = (waifu.relationship ?? 0) >= 100;
  const ringEquipped = !!(equipment.ring || equipment.ring_equipped);

  const weaponTier = equipment.weapon?.tier ?? equipment.weapon?.level ?? 1;
  const armorTier = equipment.armor?.tier ?? equipment.armor?.level ?? 1;
  const weaponName = equipment.weapon?.name ?? 'Оружие';
  const armorName = equipment.armor?.name ?? 'Доспех';

  const weaponUpgradable = (upgrade_costs?.weapon_upgrade_cost ?? 0) > 0;
  const armorUpgradable = (upgrade_costs?.armor_upgrade_cost ?? 0) > 0;

  return (
    <div className="space-y-3">
      <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
        Экипировка
      </h3>

      <div className="grid grid-cols-4 gap-2">
        <EquipSlot
          iconSrc={weaponIconPath(equipment.weapon)}
          label={weaponName}
          subLabel={`T${weaponTier}`}
          tooltip={`${weaponName} • T${weaponTier}`}
          ringColor="ring-[#c9a227]/50"
          showUpgrade={weaponUpgradable}
          onClick={() => {
            haptic('light');
            openModal('upgrade-weapon', { waifuId: waifu.id });
          }}
        />

        <EquipSlot
          iconSrc={armorIconPath(equipment.armor)}
          label={armorName}
          subLabel={`T${armorTier}`}
          tooltip={`${armorName} • T${armorTier}`}
          ringColor="ring-[#5a8fd4]/50"
          showUpgrade={armorUpgradable}
          onClick={() => {
            haptic('light');
            openModal('upgrade-armor', { waifuId: waifu.id });
          }}
        />

        <EquipSlot
          iconSrc={
            equipment.pantsu?.icon
              ? `/data/items/${equipment.pantsu.icon}`
              : null
          }
          emptyIcon="👙"
          label={equipment.pantsu?.name ?? 'Pantsu'}
          subLabel={equipment.pantsu ? '' : 'Не надето'}
          tooltip={equipment.pantsu?.name ?? 'Pantsu'}
          ringColor="ring-[#d45a8f]/50"
          onClick={() => {
            haptic('light');
            openModal('pantsu', { waifuId: waifu.id });
          }}
        />

        <button
          onClick={() => {
            haptic('light');
            openModal('ring', { waifuId: waifu.id });
          }}
          disabled={!canRing && !ringEquipped}
          className={cn(
            'aspect-square rounded-xl border flex flex-col items-center justify-between p-2 transition active:scale-[0.97] ring-1',
            ringEquipped
              ? 'bg-gold/15 border-gold/60 ring-pink-400/60'
              : canRing
                ? 'bg-bg-elevated border-white/10 hover:border-white/20 ring-white/5'
                : 'bg-bg-elevated/40 border-white/5 opacity-60 cursor-not-allowed ring-white/5',
          )}
          title={ringEquipped ? 'Обручены' : canRing ? 'Доступно' : 'Нужно ♥100'}
        >
          <img
            src="/data/items/wedding_ring.webp"
            alt="Кольцо"
            className="w-14 h-14 object-contain"
            style={{ opacity: ringEquipped ? 1 : 0.3 }}
            draggable={false}
          />
          <div className="flex flex-col items-center w-full">
            <span className="text-[11px] text-white font-medium truncate w-full text-center leading-tight">
              {ringEquipped ? 'Обручены' : 'Кольцо'}
            </span>
            <span className="text-[9px] text-white/55 leading-none">
              {ringEquipped ? '' : canRing ? 'Готово' : '♥100'}
            </span>
          </div>
        </button>
      </div>
    </div>
  );
}

function EquipSlot({
  iconSrc,
  emptyIcon,
  label,
  subLabel,
  tooltip,
  ringColor,
  showUpgrade,
  onClick,
}: {
  iconSrc: string | null;
  emptyIcon?: string;
  label: string;
  subLabel: string;
  tooltip: string;
  ringColor: string;
  showUpgrade?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={tooltip}
      className={cn(
        'relative aspect-square rounded-xl border border-white/10 bg-bg-elevated hover:border-white/25 transition active:scale-[0.97] flex flex-col items-center justify-between p-2 ring-1',
        ringColor,
      )}
    >
      {iconSrc ? (
        <img
          src={iconSrc}
          alt={label}
          className="w-14 h-14 object-contain"
          draggable={false}
        />
      ) : (
        <div className="w-14 h-14 flex items-center justify-center text-3xl opacity-50">
          {emptyIcon ?? '?'}
        </div>
      )}
      <div className="flex flex-col items-center w-full mt-0.5">
        <span className="text-[11px] text-white font-medium truncate w-full text-center leading-tight">
          {label}
        </span>
        {subLabel && (
          <span className="text-[9px] text-white/55 leading-none tabular-nums">
            {subLabel}
          </span>
        )}
      </div>
      {showUpgrade && (
        <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center leading-none shadow">
          +
        </div>
      )}
    </button>
  );
}
